/**
 * Donor checkout (GH-200, GH-240, docs/ux-flows.md).
 *
 * The screen is organised around one question — how much — and everything else is either
 * pre-answered or one tap away:
 *
 *   1. a way back out, and who is receiving the money (the identity is never collapsed: giving to
 *      the wrong organization is the one irreversible mistake available here, and it is silent
 *      when it happens)
 *   2. what the gift supports, where the organization publishes more than one answer
 *   3. four amounts under a named region label, one preselected, and a fifth cell for any other
 *   4. recognition, anonymous by default, said as a state with one control to change it
 *   5. optional cost support as a single checkbox, one tap either way
 *   6. a docked summary — the server's rows and the server's total — and the button under it
 *
 * Rewritten to the owner's screen 2 on 2026-09-02. The UX contract's ordering is unchanged; what
 * changed is that the things this screen was folding behind icons are now said in words, and the
 * total moved from inside the button to a row directly above it.
 *
 * **Three things the design asks for are deliberately absent, and each is a decision rather than
 * an omission:** the RESTRICTED badge (no such field exists on any layer, and it is a legal
 * characterization DEC-028 Q1 is open on), the "up to $X per gift" line (no server-provided figure
 * reaches this bundle — see the maximum's twin, the minimum), and the "spent only on this
 * purpose" promise (nothing here records, verifies or enforces it). They are reported rather than
 * guessed at.
 *
 * Interaction budget: no account wall, no required email or address, and removing the optional
 * cost support takes exactly one tap.
 */
import {
  ANONYMITY_COPY,
  GIVE_CARD_COPY,
  NOTE_ON_SCREEN_EXPLANATION,
  NOTE_ON_SCREEN_OFFERED,
  DONOR_NOTE_MAX_LENGTH,
  MAX_SUGGESTED_AMOUNTS,
  RECOGNITION_NAME_MAX_LENGTH,
  appealAskWords,
  formatMoney,
  cardFailureMessage,
  formatMoneyCompact,
  parseAmountToMinor,
  type ContextResponse,
  type DafGrantResponse,
  type DonorSessionResponse,
  type EngagementPromptWire,
  type DirectoryEntry,
  type DonorPledge,
  type MoneyWire,
  type QuoteResponse,
  type SavedInstrument,
} from "@givehere/contracts";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  AnonymousIcon,
  FolderIcon,
  GearIcon,
  InfoIcon,
  LocationIcon,
  NoteIcon,
  PersonIcon,
} from "./icons.js";
import { donorMessageForStripeError } from "./card-failure-message.js";
import { OrgHeader } from "./OrgHeader.js";
import { OrgInfoPanel } from "./OrgInfoPanel.js";
import { GivingTermsNote } from "./legal/GivingTermsNote.js";
import { PaymentSheet, type SheetChoice, type WalletExpressProps } from "./PaymentSheet.js";
import { paymentKeys } from "./payment-keys.js";
import { PledgeFlow } from "./PledgeFlow.js";
import { ReceiptScreen } from "./Receipt.js";
import { DafPayButton } from "./DafPay.js";
import {
  DesignationPicker,
  designationChoice,
  sendableDesignationId,
} from "./DesignationPicker.js";
import {
  ApiError,
  createPaymentIntent,
  createQuote,
  fetchContext,
  fetchEventPage,
  fetchGoal,
  fetchNearby,
  newIdempotencyKey,
} from "./api.js";
import { rememberAppealResponse } from "./appeal-response.js";
import { DonorPanel, donorName } from "./DonorPanel.js";
import { Explain, ExplainMore } from "./Explain.js";
import { forgetDisplayName, rememberDisplayName, rememberedDisplayName } from "./donor-memory.js";
import {
  fetchDonorPledges,
  fetchDonorSession,
  fetchEngagement,
  fetchPaymentMethods,
  forgetToken,
  isReturningDonor,
  storedToken,
} from "./donor-session.js";
import { readCheckoutHints } from "./give-hints.js";
import { methodsForPledgePayment, pledgeToPay } from "./pledge-funding.js";
import { devicePresentable, instrumentLabel } from "./PaymentSheet.js";
import { PlaidLinkConfirm } from "./PlaidLinkConfirm.js";
import { StripeConfirm, stripeClient, stripeConfiguredInBundle } from "./StripeConfirm.js";
import { applePayAvailable, NO_WALLET_CONTACT, type WalletContact } from "./wallets.js";
import { WalletExpressBoxes } from "./WalletExpress.js";

/** How long "Signed in as …" stays after sign-in from this page (GH-856). */
const SIGNED_IN_NOTICE_MS = 4000;

/**
 * A bank-redirect method (3-D Secure) leaves this page and comes back. The receipt's donation id
 * and access token would be lost across that hop, so they are parked in sessionStorage keyed by
 * the PaymentIntent id — same tab, minutes of lifetime, nothing durable.
 */
const handoffKey = (intentId: string) => `givehere.pay.${intentId}`;

const intentIdOf = (clientSecret: string): string => clientSecret.split("_secret")[0] ?? "";

const parkHandoff = (clientSecret: string, value: { donationId: string; accessToken: string }) => {
  try {
    window.sessionStorage.setItem(handoffKey(intentIdOf(clientSecret)), JSON.stringify(value));
  } catch {
    // Storage refused: a redirect return will land on the amount screen instead of the
    // receipt. The payment itself is unaffected — the webhook completes it either way.
  }
};

/**
 * The redirect return: Stripe appends payment_intent + redirect_status to the return URL.
 *
 * `failed` means the donor's bank check (3-D Secure) did not pass. Nothing was charged and the
 * receipt would only poll for the same news, so the return says so and the amount screen stays
 * open for another try.
 */
type RedirectReturn =
  | { readonly kind: "resume"; readonly handoff: { donationId: string; accessToken: string } }
  | { readonly kind: "authentication_failed" };

const resumeFromRedirect = (): RedirectReturn | null => {
  const params = new URLSearchParams(window.location.search);
  const intentId = params.get("payment_intent");
  const redirectStatus = params.get("redirect_status");
  if (!intentId || !redirectStatus) return null;
  try {
    const stored = window.sessionStorage.getItem(handoffKey(intentId));
    if (!stored) return null;
    const url = new URL(window.location.href);
    for (const key of ["payment_intent", "payment_intent_client_secret", "redirect_status"]) {
      url.searchParams.delete(key);
    }
    window.history.replaceState(null, "", url.toString());
    if (redirectStatus === "failed") return { kind: "authentication_failed" };
    return {
      kind: "resume",
      handoff: JSON.parse(stored) as { donationId: string; accessToken: string },
    };
  } catch {
    return null;
  }
};

/**
 * Did the donor arrive here meaning to promise rather than to pay? (owner-directed 2026-09-02)
 *
 * The event page has two buttons — **Give** and **Pledge for later** — and until now both landed
 * on this screen in the identical state, which made the second one a lie: it opened a payment
 * screen. `?intent=pledge` is the difference, and it is deliberately the weakest possible kind of
 * difference: a **presentation hint**, read once, that changes which control this screen leads
 * with. It is not trusted for anything.
 *
 * Nothing about the money moves with it. The amount is still chosen here, the quote is still the
 * server's, `PledgeFlow` still records against the signed quote token, and the hint is ignored
 * outright wherever a pledge is not on offer — an organization with no event has no promise to
 * make (DEC-042), and `paymentMethods` is what says whether `pledge` is available at all. A
 * forged parameter therefore buys nothing: at worst it re-orders two buttons on a screen the
 * donor is looking at.
 */
const arrivedToPledge = (): boolean =>
  new URLSearchParams(window.location.search).get("intent") === "pledge";

type PayMethod = "card" | "apple_pay" | "google_pay";

const PAY_METHOD_LABELS: Record<PayMethod, string> = {
  card: "card",
  apple_pay: "Apple Pay",
  google_pay: "Google Pay",
};

/**
 * One cost-support checkbox per screen, so one id is enough to bind its label to it.
 *
 * Bound rather than wrapping, and that is not a style choice: a control nested inside its own
 * `<label>` disappears from the accessibility tree this repo snapshots (`apps/semantic-snapshot.ts`
 * stops at a role named from its content), so a regression that dropped the checkbox outright would
 * leave the baseline byte-identical. `DesignationPicker` learned this first and says so at length.
 */
const COST_SUPPORT_ID = "cost-support";

/** One of each per screen, for the same binding reason as the checkbox above. */
const CUSTOM_AMOUNT_ID = "custom-amount";
const REQUEST_AMOUNT_ID = "request-amount";
/** `.amounts` is `repeat(2, 1fr)`; iOS `AmountEntry.columns` is the same two. */
const AMOUNT_COLUMNS = 2;
const SHOWN_NAME_ID = "shown-name";
const DONOR_NOTE_ID = "donor-note";
const DONOR_NOTE_COUNT_ID = "donor-note-count";

/**
 * A note as the donor types it, cut at the contract's limit in Unicode code points — the unit the
 * server and the column count (GH-793). `maxLength` on the field counts UTF-16 units and would
 * stop an emoji-heavy note at half the length the server allows.
 */
const capNote = (value: string): string => [...value].slice(0, DONOR_NOTE_MAX_LENGTH).join("");

/** A shown name, cut at the contract's limit the same way — iOS `DonorDisplayName.capped`'s twin. */
const capName = (value: string): string =>
  [...value].slice(0, RECOGNITION_NAME_MAX_LENGTH).join("");

/**
 * The note fields of a payment request (GH-793, GH-887): none for a held gift (the server refuses
 * a note there); otherwise the note, and `showNoteOnScreen: true` only when the donor ticked it
 * beside a note — an unticked box sends nothing, so the request is the one it always was.
 */
const noteRequestFields = (
  held: boolean,
  donorNote: string | null,
  showNoteOnScreen: boolean,
): { donorNote?: string | null; showNoteOnScreen?: true } => {
  if (held) return {};
  return showNoteOnScreen && donorNote !== null
    ? { donorNote, showNoteOnScreen: true }
    : { donorNote };
};

type Status =
  "loading" | "ready" | "quoting" | "paying" | "confirming" | "paid" | "pledging" | "error";

/**
 * The fee's own ⓘ, on the fee's own row (owner-directed 2026-09-02: "the (i) for each should be
 * next to it as opposed additional text or info somewhere else").
 *
 * Its own component only because the row renders in two states and both need it — factoring it
 * here is what stops the accepted state quietly losing the explanation the declined one has, which
 * is exactly the asymmetry DEC-034 exists to forbid: a screen that offers "100% supports them"
 * while saying nothing about the alternative implies the alternative is free.
 *
 * `costSupportLabel` and `costSupportDeclineNote` are the server's sentences, composed against the
 * rate this quote was priced with. Nothing here writes a word about a fee.
 */
function FeeExplain({ quote }: { quote: QuoteResponse }) {
  return (
    <ExplainMore title="Who pays the fee">
      <p>{quote.costSupportLabel}</p>
      {quote.costSupportDeclineNote && <p>{quote.costSupportDeclineNote}</p>}
    </ExplainMore>
  );
}

/**
 * A checkout answering a payment request (GH-845), from `PaymentRequestPage`. The amount opens at
 * what the request still asks and the donor may change it — part of it or more, from $1 (GH-849);
 * the designation is the request's and no picker is drawn; no pledge or DAF door is offered. The
 * server checks the amount and prices the quote; the request is credited with at most what it asks.
 */
export type CheckoutPaymentRequest = { readonly token: string; readonly amountMinor: string };

export function Checkout({
  contextToken,
  paymentRequest = null,
}: {
  contextToken: string;
  paymentRequest?: CheckoutPaymentRequest | null;
}) {
  const [status, setStatus] = useState<Status>("loading");
  const [context, setContext] = useState<ContextResponse | null>(null);
  const [quote, setQuote] = useState<QuoteResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  /**
   * Whether the failure that stopped the load is worth trying again, and the counter that does it.
   *
   * The server already says which it is — `retryability` is part of every error it returns — and
   * this screen used to throw that away and render one unconditional heading. A donor whose
   * connection dropped was told the link was not valid, which sends them to re-scan the one thing
   * that was never broken; a donor holding a genuinely dead link would be told to keep retrying.
   * The two need different words and different affordances, so the distinction is kept.
   */
  const [loadRetryable, setLoadRetryable] = useState(false);
  const [loadAttempt, setLoadAttempt] = useState(0);
  const [infoOpen, setInfoOpen] = useState(false);
  /** Set once the payment is created; hands off to the receipt screen. */
  const [paid, setPaid] = useState<{ donationId: string; accessToken: string } | null>(null);
  /** True for a bank (ACH) gift: the receipt's waiting copy speaks in days, not seconds. */
  const [paidViaBank, setPaidViaBank] = useState(false);
  /** A captured DAF grant: the sponsor pays the organization directly, so this page only thanks. */
  const [dafDone, setDafDone] = useState<DafGrantResponse | null>(null);
  /** The real rail's authorization step: set when a created intent needs the donor's card. */
  const [confirming, setConfirming] = useState<{ clientSecret: string; total: string } | null>(
    null,
  );
  /** The Plaid bank rail's authorization step: Link instead of a card element (DEC-003). */
  const [bankLink, setBankLink] = useState<{
    linkToken: string;
    donationId: string;
    accessToken: string;
    total: string;
  } | null>(null);

  // Landing back from a bank redirect (3-D Secure): skip straight to the receipt, which polls
  // the server for the webhook-confirmed truth of what happened out there.
  useEffect(() => {
    const returned = resumeFromRedirect();
    if (returned?.kind === "authentication_failed") {
      setError(cardFailureMessage("authentication_failed", "donor"));
    } else if (returned) {
      setPaid(returned.handoff);
      setStatus("paid");
    }
  }, []);

  const [amountMinor, setAmountMinor] = useState<string | null>(null);
  /**
   * What the donor said the gift is for, or null for "they have not said" (DEC-043).
   *
   * Null is not "General" — it is the absence of an answer, and the server resolves the default
   * from it. The client never picks one, never reads `isGeneral` to guess, and never carries a
   * value the context did not offer: `chosenDesignationId` below re-checks that on every quote.
   */
  const [designationId, setDesignationId] = useState<string | null>(null);
  const [customAmount, setCustomAmount] = useState("");
  const [customOpen, setCustomOpen] = useState(false);
  /** What the donor typed against a payment request (GH-849); null until they change it. */
  const [requestAmountText, setRequestAmountText] = useState<string | null>(null);
  /**
   * On by default — and off from the first quote when the donor arrived to promise. A pledge
   * carries no processing support, and `pledgeFirst` below already hides the rows; but hiding
   * them while still quoting with support on meant the quote behind "or give now instead" carried
   * a fee the donor had never been shown (TestFlight feedback, 2026-09-15). iOS prices the same
   * arrival without support from the first request, and this is the web's half of that. The
   * arrival hint alone decides, before the context loads, for the same reason it decides which
   * control leads: the donor can still add support with one tap on the offer strip if the
   * pledge turns out not to be on offer.
   */
  const [costSupport, setCostSupport] = useState(() => !arrivedToPledge());

  /**
   * Answering the appeal (GH-937): `?appealRung=<id>` from the event page's appeal card. A hint
   * like every other — the quote carries it and the **server** holds the four refusals: an
   * unknown rung (`not_found`), an appeal that is not running (`conflict`), a rung the room has
   * moved past (`conflict`, "The room has moved on to another amount."), and an amount that is
   * not the rung's own (`invalid_request`). While it is set the amount is drawn fixed, because
   * every other number is one of those refusals.
   */
  const [appealRung, setAppealRung] = useState<string | null>(
    () => readCheckoutHints(window.location.search).appealRungId,
  );
  /** The ask in the organizer's words, read from the public goal projection; decoration only. */
  const [appealAsk, setAppealAsk] = useState<string | null>(null);
  /** "The room has moved on — now at $X." and its siblings; never a dead end. */
  const [appealNotice, setAppealNotice] = useState<string | null>(null);

  /**
   * Paying a pledge (GH-809): `?pledge=<id>` from the donor's own list of promises. The id is a
   * hint until the donor's own pledge list, read with their session, names it for this event; the
   * quote then carries it and the server checks ownership, openness and the event again.
   */
  const [pledgeHint] = useState(() => pledgeToPay(window.location.search));
  const [funding, setFunding] = useState<DonorPledge | null>(null);
  const [fundingState, setFundingState] = useState<"none" | "loading" | "ready" | "failed">(() =>
    pledgeHint ? "loading" : "none",
  );

  /**
   * A note for the organization's staff (GH-793, GH-809). Optional, capped at the contract's
   * limit, never sent for a held gift (the server refuses one there) and cleared after a gift.
   */
  const [note, setNote] = useState("");
  /** "Add a note" is checked, and the field stands where the checkbox was (GH-817). */
  const [noteOpen, setNoteOpen] = useState(false);
  /**
   * "Show on the event screen", ticked by the donor under their note (GH-887). Unticked by
   * default, cleared with the note, and only ever sent with a note. It asks: the note reaches the
   * screen only after the organization approves it.
   */
  const [noteOnScreen, setNoteOnScreen] = useState(false);
  /**
   * The designation chooser is expanded under the additions line (owner 2026-10-10: behind a
   * folder icon, "NOT shown by default"). Local, like `DesignationPicker.revealed`: looking is a
   * fact about this donor's attention, not about the gift.
   */
  const [designationOpen, setDesignationOpen] = useState(false);

  /**
   * Anonymous is the default and the fallback for any unrecognised input (SPEC.md §5.2) — for a
   * **guest**. A signed-in donor's gift leads with their name on file (owner 2026-10-10, the same
   * reversal `docs/ios/DESIGN.md` §5 records for the app), applied by the effect below and never
   * over a choice the donor already made themselves. The remembered device name is still loaded
   * but not applied until the donor opens the field, so a second person on a shared device sees
   * the same anonymous form the first did.
   */
  const [publishName, setPublishName] = useState(false);
  const [displayName, setDisplayName] = useState("");
  /** The name field is open on the additions line — the cycle's middle step (owner 2026-10-10). */
  const [nameEditing, setNameEditing] = useState(false);
  /**
   * Whether the donor themselves has touched the name choice. Once they have, the signed-in
   * default below never overrides them — the mirror of iOS `GiveModel.donorChoseName`.
   */
  const donorChoseName = useRef(false);
  const [remembered] = useState(rememberedDisplayName);

  const [sheetOpen, setSheetOpen] = useState(false);
  const [walletContact, setWalletContact] = useState<WalletContact>(NO_WALLET_CONTACT);

  /**
   * The event-home half of this page (owner-directed 2026-08-17): the activities happening at
   * this event, and the donor sheet that participates in them. The donation module keeps the
   * top of the screen; these live in a compact strip beneath it.
   */
  const [prompts, setPrompts] = useState<readonly EngagementPromptWire[]>([]);
  /** Which face of the donor sheet is open: engagement chips open "menu", the account chip "profile". */
  const [panelView, setPanelView] = useState<"profile" | "menu" | null>(null);
  const [panelEntries, setPanelEntries] = useState<readonly DirectoryEntry[]>([]);
  /**
   * Being on this page checks a signed-in donor in exactly once. After that their choices
   * stand: a donor who checks out or switches events must not be dragged back here by
   * merely reopening the sheet.
   */
  const [arrivalSpent, setArrivalSpent] = useState(false);

  // "Check in somewhere else" needs a directory to pick from; fetched when the sheet opens
  // so the event page itself never pays for it. Failure just leaves the picker's empty-state
  // copy pointing at the home screen, which is where the full search lives anyway.
  useEffect(() => {
    if (panelView === null || panelEntries.length > 0) return;
    let cancelled = false;
    fetchNearby(null, 25)
      .then((response) => {
        if (!cancelled) setPanelEntries(response.results);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [panelView, panelEntries.length]);
  const [validToken, setValidToken] = useState<string | null>(null);
  const [donorSession, setDonorSession] = useState<DonorSessionResponse | null>(null);

  // Quiet session probe, exactly like Home's: a dead token renders the signed-out state,
  // never an error.
  useEffect(() => {
    const token = storedToken();
    if (!token) return;
    let cancelled = false;
    fetchDonorSession(token)
      .then((session) => {
        if (cancelled) return;
        setValidToken(token);
        setDonorSession(session);
      })
      .catch(() => {
        if (!cancelled) forgetToken();
      });
    return () => {
      cancelled = true;
    };
  }, []);

  /**
   * Named by default for a signed-in donor (owner 2026-10-10): the gift leads with the name on
   * file. Applied only while the donor has not touched the choice themselves — one tap on the
   * name control makes the gift anonymous, and nothing here ever puts a name back after that —
   * and never on a held gift (no public board), never over a name already set, never for a blank
   * profile name. Guests keep the anonymous default. iOS `GiveModel.defaultNameChoice` is the
   * twin, guard for guard.
   */
  useEffect(() => {
    if (donorChoseName.current) return;
    if (!context || context.heldGift !== null) return;
    const onFile = donorSession?.profile.displayName?.trim();
    if (!onFile || publishName || displayName !== "") return;
    setDisplayName(capName(onFile));
    setPublishName(true);
    // The quote follows through the `recognition` memo this state feeds — nothing extra to send.
  }, [donorSession, context, publishName, displayName]);

  /**
   * Whether this checkout is for a room a donor can be *in* (GH-856): a live event, not the
   * always-on general fund, not a held gift's draft fund. Only then does the profile door carry
   * a check-in, and only then is "Check in at an event" offered. Anything else — including a read
   * that failed or has not answered yet — is an organization's page, where the profile door is
   * sign-in and nothing more. The answer is the server's (`status`, `isGeneralFund`), from the
   * same public, identifier-free read the event page uses.
   */
  const [eventScoped, setEventScoped] = useState(false);
  useEffect(() => {
    const eventId = context?.event?.id;
    if (!eventId || context.heldGift !== null) {
      setEventScoped(false);
      return;
    }
    let cancelled = false;
    fetchEventPage(eventId)
      .then((page) => {
        if (!cancelled) setEventScoped(page.event.status === "live" && !page.event.isGeneralFund);
      })
      .catch(() => {
        if (!cancelled) setEventScoped(false);
      });
    return () => {
      cancelled = true;
    };
  }, [context]);

  /** "Signed in as …" — shown for a moment after sign-in from this page, then gone (GH-856). */
  const [signedInAs, setSignedInAs] = useState<string | null>(null);
  useEffect(() => {
    if (signedInAs === null) return;
    const timer = window.setTimeout(() => setSignedInAs(null), SIGNED_IN_NOTICE_MS);
    return () => window.clearTimeout(timer);
  }, [signedInAs]);
  /** Read once: a returning donor who is signed out gets a more prominent way back in. */
  const [returning] = useState(() => isReturningDonor() === true);
  /**
   * What Stripe said about the Apple Pay button leading the give box (GH-856 d): unknown until it
   * answers, and "unavailable" brings the ordinary give button back rather than leaving a gap.
   */
  const [primaryWallet, setPrimaryWallet] = useState<"unknown" | "available" | "unavailable">(
    "unknown",
  );

  // The event's activities are public reads; a non-live event simply yields none, and a page
  // with no event at all asks nothing.
  useEffect(() => {
    if (!context?.event) return;
    const eventId = context.event.id;
    let cancelled = false;
    fetchEngagement(eventId)
      .then((list) => {
        if (!cancelled) setPrompts(list.filter((prompt) => !prompt.archived));
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [context]);

  /**
   * The returning donor's way of paying, when this browser holds their remembered session and
   * their profile knows one. Strictly a head start: the Give button leads with it and "another
   * way" opens the full sheet. Loading it is best-effort — a signed-out or expired session
   * simply means the button behaves as it always did.
   */
  const [savedMethod, setSavedMethod] = useState<PayMethod | null>(null);
  /** Saved instruments for one-tap reuse (GH-330); empty for guests. */
  const [instruments, setInstruments] = useState<readonly SavedInstrument[]>([]);
  // Keyed on the *validated* token so a donor who signs in mid-checkout (through the panel)
  // sees their saved instruments without a reload.
  useEffect(() => {
    if (!validToken) {
      setSavedMethod(null);
      setInstruments([]);
      return;
    }
    let cancelled = false;
    fetchPaymentMethods(validToken)
      .then((known) => {
        if (cancelled) return;
        setSavedMethod(known.defaultMethod);
        setInstruments(known.instruments);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [validToken]);

  // The sheet is the payment-methods screen: refresh the saved list as it opens, so a card
  // saved by the gift made a minute ago shows up without a reload.
  useEffect(() => {
    if (!sheetOpen || !validToken) return;
    let cancelled = false;
    fetchPaymentMethods(validToken)
      .then((known) => {
        if (!cancelled) setInstruments(known.instruments);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [sheetOpen, validToken]);

  /**
   * One key per checkout attempt, held across retries. This is what makes a double tap safe —
   * regenerating it per click would defeat the server's idempotency entirely.
   */
  // One key per way of paying (GH-791): a double tap replays, a change of method is a new request.
  const [paymentKey] = useState(() => paymentKeys(newIdempotencyKey));

  useEffect(() => {
    let cancelled = false;
    fetchContext(contextToken, paymentRequest?.token ?? null)
      .then((resolved) => {
        if (cancelled) return;
        setContext(resolved);
        // After the event only a promise is on offer (GH-800), and a promise carries no
        // processing support — so it is priced without it from the first quote, as an arrival
        // to pledge is.
        if (
          !pledgeHint &&
          resolved.event &&
          !resolved.event.acceptingDonations &&
          resolved.event.acceptingPledges
        ) {
          setCostSupport(false);
        }
        // A reloaded context is a different offer: an id chosen against the previous one has no
        // standing here, and keeping it would be the client deciding a designation on its own.
        // The one exception is a fund tapped on the organization's page (GH-809), and only if
        // this context offers it — otherwise it is dropped here, as every quote would drop it.
        const hints = readCheckoutHints(window.location.search);
        setDesignationId(
          hints.designationId && sendableDesignationId(resolved, hints.designationId)
            ? hints.designationId
            : null,
        );
        setAmountMinor(
          // A payment request's amount (GH-845) — shown, and priced by the server from the request.
          paymentRequest?.amountMinor ??
            // An amount tapped on the organization's page (GH-809): a preselection only; the
            // server prices it like any other.
            hints.amountMinor ??
            resolved.suggestedAmount?.amountMinor ??
            resolved.event?.defaultAmount?.amountMinor ??
            resolved.event?.suggestedAmounts[0]?.amountMinor ??
            // Nothing is preselected where there is no event. A give page pre-filled with an
            // amount says "this is what people give here", and on a page for an organization
            // that has not joined there is no "here" to have a custom yet.
            null,
        );
        // A redirect return has already parked this page on the receipt; the context load must
        // not drag it back to the amount screen.
        setStatus((current) => (current === "paid" ? current : "ready"));
      })
      .catch((caught: unknown) => {
        if (cancelled) return;
        const refusal = caught instanceof ApiError ? caught : null;
        setError(refusal?.message ?? "This link is not valid.");
        setLoadRetryable(refusal?.retryability === "retryable");
        setStatus((current) => (current === "paid" ? current : "error"));
      });
    return () => {
      cancelled = true;
    };
  }, [contextToken, loadAttempt, pledgeHint, paymentRequest]);

  /**
   * Re-aim at whatever the appeal is at now, from the same public goal projection the event page
   * polls (GH-937). Called on arrival — the room may have moved while the donor walked over — and
   * after any of the server's appeal refusals, so a refusal is a re-render at the new rung with a
   * notice, never a dead end.
   *
   * `afterRefusal` settles the one ambiguous case: the projection (cached up to five seconds) can
   * still name the rung the server just refused. On arrival that reading is simply confirmation;
   * after a refusal, trusting it would re-send the same refused quote forever, so the appeal
   * stands down instead and the screen becomes the ordinary give page it already is.
   */
  const syncAppeal = useCallback(
    async (eventId: string, rungId: string, afterRefusal: boolean): Promise<void> => {
      const standDown = (notice: string) => {
        setAppealRung(null);
        setAppealAsk(null);
        setAppealNotice(notice);
      };
      let current;
      try {
        current = (await fetchGoal(eventId)).appeal?.currentRung ?? null;
      } catch {
        // The projection could not be read. On arrival the quote will say what the server
        // thinks; after a refusal, standing down beats replaying the refusal blind.
        if (afterRefusal) standDown("The appeal is not taking this amount right now.");
        return;
      }
      if (current === null) {
        standDown("The appeal has closed. You can still give.");
        return;
      }
      if (current.id === rungId) {
        if (afterRefusal) {
          standDown("The appeal is not taking this amount right now.");
          return;
        }
        setAppealAsk(appealAskWords(current));
        // Re-pin the amount to the rung's own, so a doctored `?amount=` cannot ride an appeal
        // link into a quote the server would refuse.
        setAmountMinor(current.amount.amountMinor);
        return;
      }
      // The room has moved on: answer the amount it is at now, and say so.
      setAppealRung(current.id);
      setAmountMinor(current.amount.amountMinor);
      setAppealAsk(appealAskWords(current));
      setAppealNotice(`The room has moved on — now at ${formatMoney(current.amount)}.`);
    },
    [],
  );

  /*
    Which promise this gift keeps, read from the donor's own list with their own session. The
    amount opens at what is still owed — the server's subtraction — and the donor may change it:
    paying part of a promise is allowed, and the server clamps the credit rather than refusing.
  */
  const contextEventId = context?.event?.id ?? null;
  useEffect(() => {
    if (!pledgeHint || !contextEventId) return;
    const token = storedToken();
    if (!token) {
      setFundingState("failed");
      return;
    }
    let cancelled = false;
    fetchDonorPledges(token)
      .then((pledges) => {
        if (cancelled) return;
        const found = pledges.find(
          (pledge) => pledge.pledgeId === pledgeHint && pledge.eventId === contextEventId,
        );
        if (!found) {
          setFundingState("failed");
          return;
        }
        setFunding(found);
        setFundingState("ready");
        setAmountMinor(found.outstanding.amountMinor);
        setCustomAmount(formatMoney(found.outstanding));
        setCustomOpen(true);
      })
      .catch(() => {
        if (!cancelled) setFundingState("failed");
      });
    return () => {
      cancelled = true;
    };
  }, [pledgeHint, contextEventId]);

  /*
    The appeal arrival check (GH-937): one read of the public goal projection, for the ask in the
    organizer's words and for the room having moved while the donor walked over. Not a poll — the
    quote's own refusals catch anything that moves after this — and it re-runs only when the rung
    itself changes, which `syncAppeal` makes terminal.
  */
  useEffect(() => {
    if (appealRung === null || contextEventId === null) return;
    void syncAppeal(contextEventId, appealRung, false);
  }, [appealRung, contextEventId, syncAppeal]);

  /** Sent only with the donor's session; the server refuses a pledge id without one. */
  const fundingPledgeId = funding?.pledgeId;

  /**
   * The rung a quote may name (GH-937). Never beside a `pledgeId` or a pay-link token — the
   * contract refuses the combinations (paying an earlier promise is not a new commitment) —
   * and neither of those paths arrives carrying the hint anyway.
   */
  const appealRungToSend =
    appealRung !== null && !fundingPledgeId && !paymentRequest ? appealRung : null;

  /**
   * One of the appeal's own refusals, while a rung is on the quote (GH-937): an unknown rung
   * (`not_found`), a closed or moved-on appeal (`conflict`), or an amount that is not the rung's
   * (`invalid_request`). Each one means "re-read the room", which is what `syncAppeal` does.
   */
  const isAppealRefusal = useCallback(
    (caught: unknown): boolean =>
      appealRungToSend !== null &&
      caught instanceof ApiError &&
      (caught.code === "not_found" ||
        caught.code === "conflict" ||
        caught.code === "invalid_request"),
    [appealRungToSend],
  );

  /**
   * Record this tab's answer once the gift stands (GH-937), so the event page's appeal card
   * acknowledges instead of asking again this session. "paid" is the receipt handoff — the moment
   * the thank-you screen appears — and a pledge records through `PledgeFlow`'s own callback below.
   */
  useEffect(() => {
    if (status !== "paid" || appealRungToSend === null) return;
    const eventId = context?.event?.id;
    if (!eventId || !amountMinor) return;
    rememberAppealResponse(eventId, {
      kind: "gave",
      rungId: appealRungToSend,
      amount: { amountMinor, currency: context?.event?.currency ?? "USD" },
    });
  }, [status, appealRungToSend, context, amountMinor]);

  const recognition = useMemo(
    () =>
      publishName && displayName.trim().length > 0
        ? ({ mode: "donor_name", displayName: displayName.trim() } as const)
        : ({ mode: "anonymous" } as const),
    [publishName, displayName],
  );

  /**
   * The word the recognition row shows, read off the value that actually travels.
   *
   * Derived from `recognition` and not from `publishName`, which is the difference between a
   * screen that reports the switch and a screen that reports the gift. A donor who turns
   * recognition on and has not yet typed anything is still anonymous — `recognition` above says so
   * and the server will be told so — and a line reading "Shown as your name" at that moment would
   * be the one sentence on this page that is false. It flips the instant a name is typed, which is
   * also the instant the wire value changes.
   *
   * iOS shows "Show my name" in that same in-between state (`GiveControlsRow.recognitionLabel`).
   * The two surfaces agree on the two states that reach the server and differ only on the prompt
   * in the middle; worth noting here so the next person to reconcile them knows it was seen.
   */
  const shownAs = recognition.mode === "donor_name" ? recognition.displayName : "Anonymous";

  /**
   * What this context asks — nothing, a line, or a picker — and what may travel because of it.
   *
   * Both answers come out of `DesignationPicker`, which holds the whole condition in one file.
   * Re-deriving what may be *sent* from the context on every quote, rather than trusting the state
   * variable alone, makes the two refusals true by construction instead of by care: a context that
   * reloads into a locked event cannot leave a stale id attached, and a pinned designation is
   * never echoed back at the server that pinned it.
   */
  const choice = useMemo(
    () => (context ? designationChoice(context) : ({ kind: "none" } as const)),
    [context],
  );
  const chosenDesignationId = context ? sendableDesignationId(context, designationId) : null;

  // Re-quote whenever anything that affects money changes. The client never adjusts a total
  // itself; it asks the server for a new one.
  useEffect(() => {
    // Guarded on `context` rather than `status` so the dependency list is genuinely complete —
    // suppressing the exhaustive-deps rule here would hide the stale-closure bugs it exists to
    // catch, in the one component that handles money.
    // Nothing to price where there is no event: the server refuses to quote such a context
    // (`requireEventId`), and asking anyway would put a failed request behind every keystroke.
    if (!context || !context.event || !amountMinor) return;
    // Not until we know whether this gift keeps a promise: a quote without the pledge would
    // price a different gift, and on an event that has ended the server would refuse it.
    if (fundingState === "loading") return;
    let cancelled = false;
    const timer = setTimeout(() => {
      setStatus("quoting");
      createQuote(
        {
          contextToken,
          amountMinor,
          methodClass: "card",
          // A held gift offers no cost support (GH-798): the server ignores the flag and quotes
          // zero, and asking for it anyway would put a choice on the wire the donor was never
          // shown.
          costSupportSelected: costSupport && context.heldGift === null,
          recognition,
          // The amount does not move when this changes — the receipt copy and the confirmation
          // line do, and the server is the one that says so. So a designation re-quotes exactly
          // as an amount does, rather than being held back until payment.
          designationId: chosenDesignationId,
          ...(fundingPledgeId ? { pledgeId: fundingPledgeId } : {}),
          ...(paymentRequest ? { paymentRequestToken: paymentRequest.token } : {}),
          ...(appealRungToSend ? { appealRungId: appealRungToSend } : {}),
        },
        fundingPledgeId ? storedToken() : null,
      )
        .then((next) => {
          if (cancelled) return;
          setQuote(next);
          setStatus("ready");
          setError(null);
        })
        .catch((caught: unknown) => {
          if (cancelled) return;
          setQuote(null);
          // An appeal refusal is a fact about the room, not this donor's mistake: re-read the
          // appeal and re-render at whatever it is at now (GH-937), with syncAppeal's own notice
          // instead of the refusal's sentence. Never a dead end.
          if (isAppealRefusal(caught) && context.event && appealRungToSend) {
            setStatus("ready");
            void syncAppeal(context.event.id, appealRungToSend, true);
            return;
          }
          setError(caught instanceof ApiError ? caught.message : "Could not calculate the total.");
          setStatus("ready");
        });
    }, 150);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [
    contextToken,
    amountMinor,
    costSupport,
    recognition,
    context,
    chosenDesignationId,
    fundingState,
    fundingPledgeId,
    paymentRequest,
    appealRungToSend,
    isAppealRefusal,
    syncAppeal,
  ]);

  /** The note as it travels: trimmed, null when blank, and absent for a held gift. */
  const noteToSend = context?.heldGift ? null : note.trim() || null;
  /** The tick travels only beside a note; with none there is nothing to show (GH-887). */
  const showNoteOnScreen = noteToSend !== null && noteOnScreen;
  /**
   * The methods this screen offers. Paying a pledge offers card-class rails whatever became of the
   * event (`pledge-funding.ts`); every other gift reads the server's list as it stands.
   */
  const offeredMethods = useMemo(
    () =>
      context
        ? paymentRequest
          ? // Answering a request is a payment: a promise is not on offer (GH-845).
            context.paymentMethods.filter((option) => option.id !== "pledge")
          : funding
            ? methodsForPledgePayment(context)
            : context.paymentMethods
        : [],
    [context, funding, paymentRequest],
  );
  const heldGiftCheckout = (context?.heldGift ?? null) !== null;
  /** The note and the tick as they ride on a payment request: absent for a held gift. */
  const noteFields = useMemo(
    () => noteRequestFields(heldGiftCheckout, noteToSend, showNoteOnScreen),
    [heldGiftCheckout, noteToSend, showNoteOnScreen],
  );

  /**
   * The owner's tap cycle on the name control (2026-10-10), the mirror of iOS `GiveCard.cycleName`:
   * named → anonymous → the field, prefilled and focused → back to the name on file → … A guest
   * with no name on file has nothing to return to, so their third step lands on what they typed,
   * or on anonymous when the field is blank.
   *
   * A cycle tap never forgets the remembered device name — the donor may be cycling straight back
   * to it. "Not you? Clear" below the line is what forgets (`donor-memory.ts` has why that
   * matters on a phone that gets passed around), and every tap marks the choice as the donor's,
   * so a profile that loads later never overrides it.
   */
  const nameOnFile = donorSession?.profile.displayName?.trim() || null;
  const cycleName = useCallback(() => {
    donorChoseName.current = true;
    if (nameEditing) {
      // Third tap: back to the name on file, or to what the field holds.
      setNameEditing(false);
      if (nameOnFile) {
        setDisplayName(capName(nameOnFile));
        setPublishName(true);
      } else {
        setDisplayName((typed) => {
          if (typed.trim() === "") setPublishName(false);
          return typed;
        });
      }
      return;
    }
    if (publishName && displayName.trim() !== "") {
      // First tap: anonymous. The typed name is kept so the cycle can come back to it.
      setPublishName(false);
      return;
    }
    // Second tap: the field, prefilled with whatever is nearest a name.
    setDisplayName((typed) => typed || (nameOnFile ? capName(nameOnFile) : remembered));
    setPublishName(true);
    setNameEditing(true);
  }, [nameEditing, nameOnFile, publishName, displayName, remembered]);

  const pay = useCallback(
    async (method: PayMethod) => {
      if (!quote) return;
      setStatus("paying");
      try {
        if (publishName && displayName.trim()) rememberDisplayName(displayName);

        const created = await createPaymentIntent(
          {
            quoteToken: quote.quoteToken,
            displayedTotal: quote.total,
            chosenMethod: method,
            ...noteFields,
          },
          paymentKey.keyFor(method, quote.quoteToken, noteToSend, showNoteOnScreen),
          // Read fresh, not the mount-time capture: a donor who signs in mid-checkout
          // (through the event-hub panel) must still get this gift linked to their profile.
          storedToken(),
        );
        const receipt = { donationId: created.donationId, accessToken: created.accessToken };
        setPaid(receipt);
        setSheetOpen(false);

        // On the real rail the intent now needs the donor's authorization — the Payment
        // Element step. On the synthetic rail there is nothing to authorize and the receipt
        // screen's simulate button plays the bank. Either way, "received" only ever comes from
        // the webhook the receipt screen polls for (SPEC.md §5.4).
        const liveRail = offeredMethods.some(
          (option) => option.id === "card" && option.available && option.mode === "live",
        );
        if (liveRail && created.clientSecret && stripeConfiguredInBundle()) {
          parkHandoff(created.clientSecret, receipt);
          setConfirming({ clientSecret: created.clientSecret, total: formatMoney(quote.total) });
          setStatus("confirming");
        } else {
          setStatus("paid");
        }
      } catch (caught: unknown) {
        setError(caught instanceof ApiError ? caught.message : "Payment could not be started.");
        setStatus("ready");
      }
    },
    [
      quote,
      publishName,
      displayName,
      offeredMethods,
      noteFields,
      noteToSend,
      showNoteOnScreen,
      paymentKey,
    ],
  );

  /**
   * Paying by bank re-quotes at the ach rate first — ACH costs less to process, and the donor
   * must see the bank total before authorizing it — then follows whichever rail the platform
   * is on: Plaid Link (primary) or the Stripe bank element (the admin's backup switch).
   */
  const payWithBank = useCallback(async () => {
    if (!amountMinor || !context) return;
    setStatus("paying");
    try {
      if (publishName && displayName.trim()) rememberDisplayName(displayName);
      const bankQuote = await createQuote(
        {
          contextToken,
          amountMinor,
          methodClass: "ach",
          costSupportSelected: costSupport,
          recognition,
          designationId: chosenDesignationId,
          ...(fundingPledgeId ? { pledgeId: fundingPledgeId } : {}),
          ...(paymentRequest ? { paymentRequestToken: paymentRequest.token } : {}),
          // The rung rides every re-quote (GH-937): the ACH total differs, the commitment does not.
          ...(appealRungToSend ? { appealRungId: appealRungToSend } : {}),
        },
        fundingPledgeId ? storedToken() : null,
      );
      const created = await createPaymentIntent(
        {
          quoteToken: bankQuote.quoteToken,
          displayedTotal: bankQuote.total,
          ...noteFields,
        },
        // A bank press re-quotes every time, so each press is its own request (GH-809).
        paymentKey.keyFor("bank", bankQuote.quoteToken, noteToSend, showNoteOnScreen),
        // Read fresh, not the mount-time capture: a donor who signs in mid-checkout (through
        // the event-hub panel) must still get this gift linked to their profile.
        storedToken(),
      );
      const receipt = { donationId: created.donationId, accessToken: created.accessToken };
      setPaid(receipt);
      setPaidViaBank(true);
      setSheetOpen(false);
      const total = formatMoney(bankQuote.total);

      if (created.bankRail === "plaid" && created.plaidLinkToken) {
        setBankLink({ linkToken: created.plaidLinkToken, ...receipt, total });
        setStatus("confirming");
      } else if (created.clientSecret && stripeConfiguredInBundle()) {
        parkHandoff(created.clientSecret, receipt);
        setConfirming({ clientSecret: created.clientSecret, total });
        setStatus("confirming");
      } else {
        setStatus("paid");
      }
    } catch (caught: unknown) {
      // The bank press re-quotes, so the room may have moved since the card quote (GH-937).
      if (isAppealRefusal(caught) && context.event && appealRungToSend) {
        setStatus("ready");
        void syncAppeal(context.event.id, appealRungToSend, true);
        return;
      }
      setError(caught instanceof ApiError ? caught.message : "Payment could not be started.");
      setStatus("ready");
    }
  }, [
    amountMinor,
    context,
    contextToken,
    costSupport,
    recognition,
    publishName,
    displayName,
    chosenDesignationId,
    fundingPledgeId,
    paymentRequest,
    appealRungToSend,
    isAppealRefusal,
    syncAppeal,
    noteFields,
    noteToSend,
    showNoteOnScreen,
    paymentKey,
  ]);

  /**
   * The wallet buttons' half of the deferred-intent handshake (`WalletExpress.tsx`): the
   * server-side intent is minted only inside the wallet's confirm step, from the same signed
   * quote as every other path. The receipt handles are parked exactly as `pay()` parks them, so
   * a 3-D Secure bounce out of a wallet resumes on the receipt too.
   */
  const walletCreateIntent = useCallback(
    async (method: PayMethod): Promise<{ clientSecret: string | null }> => {
      if (!quote) throw new Error("No quote");
      if (publishName && displayName.trim()) rememberDisplayName(displayName);
      const created = await createPaymentIntent(
        {
          quoteToken: quote.quoteToken,
          displayedTotal: quote.total,
          chosenMethod: method,
          ...noteFields,
        },
        paymentKey.keyFor(method, quote.quoteToken, noteToSend, showNoteOnScreen),
        // Read fresh, not the mount-time capture: a donor who signs in mid-checkout (through
        // the event-hub panel) must still get this gift linked to their profile.
        storedToken(),
      );
      const receipt = { donationId: created.donationId, accessToken: created.accessToken };
      setPaid(receipt);
      if (created.clientSecret) parkHandoff(created.clientSecret, receipt);
      return { clientSecret: created.clientSecret };
    },
    [quote, publishName, displayName, noteFields, noteToSend, showNoteOnScreen, paymentKey],
  );

  const walletAuthorized = useCallback(() => {
    setSheetOpen(false);
    setStatus("paid");
  }, []);

  /**
   * The wallet's authorization failed after the server opened the payment (GH-809). The next
   * press is a new attempt under a new key — the server supersedes the unpaid one on the same
   * quote (GH-791) — and nothing is handed to a receipt.
   */
  const walletFailed = useCallback(() => {
    paymentKey.reset();
    setPaid(null);
  }, [paymentKey]);

  /**
   * A wallet choice and a card choice both create the same server-side payment intent; what
   * differs is how the donor authorizes it in the browser.
   *
   * On the synthetic rail no wallet sheet is presented, so nothing populates `walletContact` and
   * the pledge path falls through to asking for a number. When Stripe is enabled this is where the
   * wallet's returned contact is captured — as a pre-fill only. See `wallets.ts`.
   */
  /**
   * One tap on a saved instrument (GH-330). The server charges the stored method reference and
   * confirms; this browser holds nothing chargeable. A card quote is already in hand; a saved
   * bank account re-quotes at the ACH rate first, exactly as the Bank box does. 3-D Secure,
   * when a bank insists, completes in place via the next-action step — and either way the
   * receipt screen polls the webhook-confirmed truth.
   */
  const paySaved = useCallback(
    async (instrument: SavedInstrument) => {
      if (!context || !amountMinor) return;
      setStatus("paying");
      setError(null);
      try {
        let chargeQuote = quote;
        if (instrument.kind === "us_bank_account") {
          chargeQuote = await createQuote(
            {
              contextToken,
              amountMinor,
              methodClass: "ach",
              costSupportSelected: costSupport,
              recognition,
              designationId: chosenDesignationId,
              ...(fundingPledgeId ? { pledgeId: fundingPledgeId } : {}),
              ...(paymentRequest ? { paymentRequestToken: paymentRequest.token } : {}),
              // The rung rides the saved-bank re-quote too (GH-937).
              ...(appealRungToSend ? { appealRungId: appealRungToSend } : {}),
            },
            fundingPledgeId ? storedToken() : null,
          );
        }
        if (!chargeQuote) {
          setStatus("ready");
          return;
        }
        if (publishName && displayName.trim()) rememberDisplayName(displayName);

        const created = await createPaymentIntent(
          {
            quoteToken: chargeQuote.quoteToken,
            displayedTotal: chargeQuote.total,
            savedInstrumentId: instrument.id,
            ...(instrument.kind === "card" ? { chosenMethod: "card" as const } : {}),
            ...noteFields,
          },
          // Per quote too: a saved bank re-quotes on every press (GH-809).
          paymentKey.keyFor(
            `saved:${instrument.id}`,
            chargeQuote.quoteToken,
            noteToSend,
            showNoteOnScreen,
          ),
          storedToken(),
        );
        const receipt = { donationId: created.donationId, accessToken: created.accessToken };

        if (created.state === "requires_action" && created.clientSecret) {
          // The bank wants the donor present for a step (3-D Secure). Show it, in place.
          const client = await stripeClient();
          if (client) {
            const result = await client.handleNextAction({ clientSecret: created.clientSecret });
            if (result.error) {
              // The next press is a new attempt (GH-809): replaying this key would hand back the
              // intent whose authorization just failed.
              paymentKey.reset();
              setError(donorMessageForStripeError(result.error));
              setStatus("ready");
              return;
            }
          }
        }

        setPaid(receipt);
        setPaidViaBank(instrument.kind === "us_bank_account");
        setSheetOpen(false);
        setStatus("paid");
      } catch (caught: unknown) {
        // A saved bank re-quotes on every press, so the room may have moved (GH-937).
        if (isAppealRefusal(caught) && context.event && appealRungToSend) {
          setSheetOpen(false);
          setStatus("ready");
          void syncAppeal(context.event.id, appealRungToSend, true);
          return;
        }
        setError(caught instanceof ApiError ? caught.message : "Payment could not be started.");
        setStatus("ready");
      }
    },
    [
      context,
      amountMinor,
      quote,
      contextToken,
      costSupport,
      recognition,
      publishName,
      displayName,
      chosenDesignationId,
      fundingPledgeId,
      paymentRequest,
      appealRungToSend,
      isAppealRefusal,
      syncAppeal,
      noteFields,
      noteToSend,
      showNoteOnScreen,
      paymentKey,
    ],
  );

  const choose = useCallback(
    (choice: SheetChoice) => {
      if (choice === "pledge") {
        setSheetOpen(false);
        setStatus("pledging");
        return;
      }
      if (choice === "bank") {
        void payWithBank();
        return;
      }
      setWalletContact(NO_WALLET_CONTACT);
      void pay(choice);
    },
    [pay, payWithBank],
  );

  if (status === "loading") return <p className="notice">Loading…</p>;

  // Before the context guard on purpose: a redirect return lands here with the context still
  // loading, and the receipt needs only the donation id and access token it carried across.
  if (status === "paid" && paid) {
    return (
      <ReceiptScreen
        donationId={paid.donationId}
        accessToken={paid.accessToken}
        eventId={context?.event?.id ?? null}
        organizationId={context?.organization.id ?? null}
        bankPending={paidViaBank}
        onBackToEvent={() => {
          paymentKey.reset();
          // The note belonged to the gift just made; the next one starts without it.
          setNote("");
          setNoteOpen(false);
          setNoteOnScreen(false);
          setPaid(null);
          setPaidViaBank(false);
          setConfirming(null);
          setStatus(context ? "ready" : "loading");
        }}
      />
    );
  }

  if (status === "error" || !context) {
    return (
      <div className="notice notice--error" role="alert">
        <h1>{loadRetryable ? "Could not load this page" : "This link is not valid"}</h1>
        <p>{error ?? "Scan the code again."}</p>
        {loadRetryable ? (
          <button
            type="button"
            className="pay"
            onClick={() => {
              setError(null);
              setLoadRetryable(false);
              setStatus("loading");
              setLoadAttempt((attempt) => attempt + 1);
            }}
          >
            Try again
          </button>
        ) : null}
        <a className="home-link" href="/">
          <LocationIcon />
          <span>Find an organization</span>
        </a>
      </div>
    );
  }

  if (status === "confirming" && bankLink) {
    return (
      <PlaidLinkConfirm
        linkToken={bankLink.linkToken}
        donationId={bankLink.donationId}
        accessToken={bankLink.accessToken}
        amountLabel={bankLink.total}
        organizationDisplayName={context.organization.displayName}
        onAuthorized={() => {
          setBankLink(null);
          setStatus("paid");
        }}
        onCancel={() => {
          paymentKey.reset();
          setBankLink(null);
          setPaid(null);
          setStatus("ready");
        }}
      />
    );
  }

  if (status === "confirming" && confirming) {
    return (
      <StripeConfirm
        clientSecret={confirming.clientSecret}
        amountLabel={confirming.total}
        organizationDisplayName={context.organization.displayName}
        onAuthorized={() => {
          setConfirming(null);
          setStatus("paid");
        }}
        onCancel={() => {
          // The created intent is abandoned, not reused: a fresh attempt needs a fresh
          // idempotency key, or the server correctly returns this same abandoned attempt.
          paymentKey.reset();
          setConfirming(null);
          setPaid(null);
          setStatus("ready");
        }}
      />
    );
  }

  if (status === "pledging" && quote) {
    return (
      <PledgeFlow
        amount={quote.donation}
        quoteToken={quote.quoteToken}
        organizationDisplayName={context.organization.displayName}
        suggestedPhone={walletContact.phone}
        suggestedName={publishName ? displayName : undefined}
        onCancel={() => setStatus("ready")}
        onRecorded={() => {
          // The hand is up (GH-937): remember it for this tab, so the event page's appeal card
          // acknowledges instead of asking again. The quote carried the rung; the server counted.
          const eventId = context.event?.id;
          if (appealRungToSend !== null && eventId) {
            rememberAppealResponse(eventId, {
              kind: "pledged",
              rungId: appealRungToSend,
              amount: quote.donation,
            });
          }
        }}
      />
    );
  }

  if (dafDone) {
    return (
      <main className="card">
        <OrgHeader
          organization={context.organization}
          eventName={context.event?.name ?? null}
          season={context.season}
        />
        <h1 className="daf-thanks__title">Grant on its way</h1>
        {/* Composed server-side: no tax-deduction language, no pledge language — the donor's
            deduction happened when they funded their DAF (IRS Notice 2017-73 territory). */}
        <p>{dafDone.acknowledgment}</p>
        <p className="muted">
          Your DAF sponsor may take a few days to review and send the funds. You can cancel or track
          the grant from your DAF provider&apos;s own site.
        </p>
        <button type="button" className="pay" onClick={() => setDafDone(null)}>
          Done
        </button>
      </main>
    );
  }

  /*
    A gift held for an organization that has not claimed its listing (GH-798, DEC-046). The
    organization still cannot accept donations — truthfully, the fund does — so this is the one
    context that takes money while `canAcceptDonations` is false. Everything that path does not
    offer hangs off this one value: no cost support, no designation, no pledge, no public name, no
    saved instrument. The server refuses each of those on its own account too; hiding them here is
    so the donor is never shown a choice that would be refused.
  */
  const held = context.heldGift;

  /*
    The pledge-first arrival, and the three things that have to be true for it to mean anything.

    The hint alone is never enough: there must be an **event** to promise against, and the
    **server** must be offering `pledge` in `paymentMethods`. Both are read here rather than
    assumed, so a link carrying `?intent=pledge` to a screen where a pledge is impossible simply
    behaves as an ordinary give screen instead of offering a control that would be refused.
  */
  /*
    After the event (owner-directed 2026-10-01, GH-800): an ended or archived event takes a
    promise and no money. The server says so — `acceptingDonations` false, `acceptingPledges`
    true, every paying rail unavailable — and the page leads with the promise whoever arrived.
  */
  /*
    Keeping a promise (GH-809) is a payment, never another promise: none of the pledge-first
    presentation applies, whatever the event's state.
  */
  // Answering a payment request (GH-845) is a payment in the same sense: never a promise.
  const payingPledge = funding !== null || paymentRequest !== null;
  const pledgeOnly =
    !payingPledge &&
    held === null &&
    context.event !== null &&
    !context.event.acceptingDonations &&
    context.event.acceptingPledges &&
    context.paymentMethods.some((method) => method.id === "pledge" && method.available);
  const pledgeFirst =
    pledgeOnly ||
    (!payingPledge &&
      held === null &&
      arrivedToPledge() &&
      context.event !== null &&
      context.paymentMethods.some((method) => method.id === "pledge" && method.available));

  /*
    An eventless context lands here too. It used to open a page for saying what you would give to
    an organization that has not claimed its listing; that is archived (GH-794), so a stale one
    gets the same unclaimed wording as any other page that cannot take money, and nothing to press.
  */
  if (
    !(context.organization.canAcceptDonations || held !== null) ||
    // Paying a pledge answers to the organization alone (owner, 2026-10-01; GH-800 addendum).
    !(context.event?.acceptingDonations || pledgeOnly || payingPledge)
  ) {
    return (
      <main className="card" role="alert">
        <OrgHeader
          organization={context.organization}
          eventName={context.event?.name ?? null}
          season={context.season}
        />
        <p className="badge badge--muted">{context.organization.verificationLabel}</p>
        <p>Not able to accept donations yet.</p>
        <a className="home-link" href="/">
          <LocationIcon />
          <span>Find another organization</span>
        </a>
      </main>
    );
  }

  const busy = status === "quoting" || status === "paying";
  /* The presets are the server's, never composed here. An appeal answer offers none at all:
     the amount is the rung's and the server refuses every other number (GH-937). */
  const amounts =
    paymentRequest || appealRung !== null
      ? []
      : (context.event?.suggestedAmounts ?? []).slice(0, MAX_SUGGESTED_AMOUNTS);
  // Presets and "Another amount…". Odd, and the last of them spans the row (`.amount--wide`) so
  // the block is a rectangle for any count the server sends. The recognition cell left the grid on
  // 2026-10-10 (owner): the name now lives on the additions line below, beside the note and the
  // designation icons.
  const cellCount = amounts.length + 1;
  const spanningLastCell = cellCount % AMOUNT_COLUMNS === 1 ? " amount--wide" : "";
  /**
   * What the sheet puts at the top of itself, and the only thing that decides whether it can open:
   * the quote — the server's arithmetic, which is the only arithmetic this bundle is allowed to
   * show. Nothing here adds anything to anything.
   */
  const sheetAmount: { total: MoneyWire; donation: MoneyWire } | null = quote
    ? { total: quote.total, donation: quote.donation }
    : null;
  /** The real rail can draw actual wallet buttons on the sheet; the synthetic one cannot. */
  const liveCardRail = offeredMethods.some(
    (option) => option.id === "card" && option.available && option.mode === "live",
  );
  const walletExpress: WalletExpressProps | null =
    liveCardRail && stripeConfiguredInBundle() && quote
      ? {
          amountMinor: quote.total.amountMinor,
          currency: quote.total.currency,
          createIntent: walletCreateIntent,
          onAuthorized: walletAuthorized,
          onFailed: walletFailed,
        }
      : null;

  /**
   * A saved instrument leads the Give button outright (owner-directed 2026-08-18): the fastest
   * repeat gift is one tap on the main button, charging the card or bank on file server-side.
   * The list arrives most-recently-used first, so the leader is the instrument from their last
   * gift. Only an instrument whose method the server offers here qualifies — a saved bank
   * account is no shortcut where the bank rail is off. "Another way" still opens the sheet.
   */
  const defaultInstrument =
    held !== null
      ? null
      : (instruments.find((instrument) =>
          offeredMethods.some(
            (method) =>
              method.available &&
              (instrument.kind === "us_bank_account" ? method.id === "bank" : method.id === "card"),
          ),
        ) ?? null);

  /**
   * The saved method leads the Give button only when it is genuinely usable right now: the
   * server permits it here AND this device can present it. A donor whose last gift was Apple
   * Pay, opening the page on an Android, gets the ordinary sheet — never a dead button.
   */
  const defaultMethod =
    // An instrument on file outranks the remembered-method shortcut — a shortcut that still
    // asks for the card number is no shortcut at all.
    held === null &&
    !defaultInstrument &&
    savedMethod &&
    offeredMethods.some((method) => method.id === savedMethod && method.available) &&
    devicePresentable(savedMethod)
      ? savedMethod
      : null;

  /**
   * Apple Pay leads on an Apple device that can present it (GH-856 d), when nothing the donor
   * chose outranks it — a saved card or bank, or a way of paying they made their default. The
   * server must permit it here and the device must be able to present it, the same two-part rule
   * as every wallet button. Not on a promise: nothing is paid there.
   */
  const applePayFirst =
    held === null &&
    !pledgeFirst &&
    !defaultInstrument &&
    !defaultMethod &&
    offeredMethods.some((method) => method.id === "apple_pay" && method.available) &&
    applePayAvailable();
  /** Signed out on an ordinary gift: offer the way to a saved card (GH-856 c). */
  const offerSignIn = donorSession === null && held === null && !pledgeFirst;

  /**
   * The button's chooser segment — a gear since 2026-10-10 (owner: the sheet behind it holds more
   * than payment methods), in place of the old "or choose another way to pay" line. Drawn exactly
   * where that line was drawn: when the main button does something other than open the sheet.
   */
  const showsChooser =
    !pledgeFirst && (defaultInstrument !== null || defaultMethod !== null || applePayFirst);

  /**
   * "Pledge instead" at the button's foot (owner 2026-10-10): wherever the organization offers
   * the rail and the button itself is not already the promise. `offeredMethods` has already
   * dropped `pledge` for a payment request and for a kept promise — both are payments, never a
   * new commitment.
   */
  const showsPledgeLink =
    !pledgeFirst &&
    held === null &&
    offeredMethods.some((method) => method.id === "pledge" && method.available);

  return (
    <main className="card">
      {/*
        The way back out, which this screen has never had (owner's screens, 2026-09-02).

        The owner's design draws it as a chevron in the header. It is drawn *above* the header
        instead, as the `.back` control the pledge screen already uses, because the header itself is
        a pinned owner decision from 2026-08-31 — the lockup is centred and `apps/surface-structure.test.ts`
        holds it there in both season states — and threading a leading control into a centred block
        is the exact collision that test was written after. A separate row costs one line and
        breaks nothing.

        **Where it goes, and why it is not `history.back()`.** Half the arrivals here have no
        history to go back to: a donor who scanned the code taped to a table opened this page
        first. So it names a destination rather than a direction. That destination is the event
        page, and the label is the one the menu on this same screen already uses for the same
        address (`DonorPanel` menuLinks below) — two controls on one screen that open one address
        and call it two things is how the next reader concludes one of them is broken.
      */}
      {/*
        A held gift's event is the draft holding fund (GH-798), which has no page of its own to go
        back to: its way out is the organization's page, where the donor pressed Give (GH-809).
      */}
      {held !== null ? (
        <a className="back back--out" href={`/o/${encodeURIComponent(context.organization.id)}`}>
          <span aria-hidden="true">‹</span> Organization page
        </a>
      ) : (
        context.event && (
          <a className="back back--out" href={`/e/${encodeURIComponent(context.event.id)}`}>
            <span aria-hidden="true">‹</span> Event page
          </a>
        )
      )}
      <OrgHeader
        organization={context.organization}
        eventName={context.event?.name ?? null}
        season={context.season}
        onOpenMenu={() => setPanelView("menu")}
        onOpenProfile={() => setPanelView("profile")}
        remembered={donorSession !== null}
        profileLabel={eventScoped ? "Check in at this event" : "Sign in"}
      />

      {/*
        The quiet half of "you are signed in" (GH-856): the existing notice, as a status line, for a
        few seconds after sign-in from this page. The loud half is the give button itself, which
        now leads with the donor's saved card. The profile icon's dot says the rest.
      */}
      {signedInAs !== null && (
        <p className="notice signed-in-notice" role="status">
          Signed in as <bdi>{signedInAs}</bdi>
        </p>
      )}

      {/*
        Keeping a promise (GH-809). The figure is the server's `outstanding`; the amount below opens
        at it and the donor may change it. Never called a balance: a pledge is not money received.
      */}
      {funding && (
        <p className="notice pledge-paying" role="status">
          Paying your pledge · {formatMoney(funding.outstanding)} still to pay
        </p>
      )}
      {fundingState === "failed" && (
        <p className="notice notice--error" role="alert">
          That pledge could not be found. Sign in to pay it.
        </p>
      )}

      {/*
        Answering the appeal (GH-937): what the stage is asking for, in the organizer's own words
        where they named it. The line stands above the amount it explains, and the amount below is
        fixed — the server refuses a rung quote at any other number, so there is nothing to edit.
      */}
      {appealRung !== null && (
        <p className="notice appeal-answering" role="status" dir="auto">
          {appealAsk !== null
            ? `Answering the appeal — ${appealAsk}`
            : "Answering the appeal — the amount is the one being called."}
        </p>
      )}
      {/*
        The room moved, or the appeal closed, between the tap and the quote: the screen says what
        changed and keeps working at the new rung (or as an ordinary gift). Never a dead end.
      */}
      {appealNotice !== null && (
        <p className="notice" role="status">
          {appealNotice}
        </p>
      )}

      {/* Amount. The default is preselected so a donor can give without choosing anything. */}
      <fieldset className="block">
        {/*
          The region label, visible, in the overline slot (owner's screen 2, 2026-09-02).

          It was `visually-hidden` — a name for the group and nothing on the screen. Lantern's rule
          settles which slot it belongs in: an overline *names a region* and a sentence *explains a
          choice* (`styles.css`, `.home__section-note`). "Amount" names a region, so it is
          uppercased by CSS rather than in the DOM — `text-transform` is locale-independent and
          would get a Turkish dotless i wrong, and the accessible name should stay the word a
          person would say.

          The group's accessible name therefore changes from "Choose an amount" to "Amount". That
          is the whole diff in the semantic snapshots for this element, and it is the shorter, more
          accurate of the two: this group is not the only place a donor chooses an amount.

          Deliberately **not** paired with a second overline over the picker above. The owner's
          "DIRECT YOUR GIFT" would label a region that already has a heading — the question
          `DESIGNATION_QUESTION` asks — and one region cannot be labelled twice. See the report;
          that one is an owner's call, not an implementation detail.
        */}
        <legend className="give__section">Amount</legend>
        {/*
          Two columns, and a last row that is never half empty (owner-directed 2026-09-16, the
          same rule iOS `AmountEntry` applies). The grid holds the presets, "Another amount…", and —
          off the pledge-only path — the recognition cell; when that count is odd the last cell
          spans the row rather than leaving a lone tile beside a gap.
        */}
        <div className="amounts">
          {amounts.map((suggested) => (
            <button
              key={suggested.amountMinor}
              type="button"
              className={
                amountMinor === suggested.amountMinor ? "amount amount--selected" : "amount"
              }
              aria-pressed={amountMinor === suggested.amountMinor}
              onClick={() => {
                setAmountMinor(suggested.amountMinor);
                setCustomAmount("");
                setCustomOpen(false);
              }}
            >
              {formatMoneyCompact(suggested)}
            </button>
          ))}
          {/*
            The custom amount, as the last cell of the grid rather than an unlabelled pencil on the
            row below (owner's screen 2).

            The pencil was an icon-only control whose meaning lived entirely in an `aria-label` —
            visible to a screen reader and a guess for everybody else, and the guess mattered
            because this is the only way to give a figure the organization did not suggest. As a
            cell it reads as what it is: a fifth choice in the row of choices, dashed rather than
            solid because pressing it opens a field instead of setting a number.

            The accessible name is now the visible text, which is what WCAG 2.5.3 asks for and what
            the icon could never satisfy — "Enter a different amount" was speakable only by someone
            who could not see the control. `aria-expanded` stays: it is what says the field below
            belongs to this press.
          */}
          {/*
            Half a row, and the field opens **inside** the cell (owner-directed 2026-09-02).

            It used to span the full width and push a separate `.field` underneath it, which cost
            two rows of a phone screen for one number and separated the control from the thing it
            controlled — a donor tapping "Another amount…" watched a box appear somewhere else. The
            cell now becomes the box: same grid position, same size, no reflow of anything below.

            `aria-expanded` is gone with the disclosure it described, because there is no longer a
            region below that belongs to this press. The field carries its own label instead.
          */}
          {appealRung !== null ? (
            /*
              The rung's amount, fixed (GH-937). Not an input and not a button: a commitment at
              $1,000 is $1,000 — the ladder counts each answer at its rung's amount, and the
              server refuses a quote at any other number. Drawn as the selected cell so it reads
              as the chosen amount rather than as a control that stopped working.
            */
            <div
              className={`amount amount--selected amount--appeal${spanningLastCell}`}
              aria-label="Amount, set by the appeal"
            >
              {amountMinor !== null
                ? formatMoneyCompact({
                    amountMinor,
                    currency: context.event?.currency ?? "USD",
                  })
                : "—"}
            </div>
          ) : paymentRequest ? (
            /*
              The request's amount (GH-849): what is still asked, filled in, and the donor's to
              change — part of it, or more (owner, 2026-10-02). Any amount from $1; the server
              checks it and credits the request with at most what it still asks.
            */
            <div className={`amount amount--field${spanningLastCell}`}>
              <label className="visually-hidden" htmlFor={REQUEST_AMOUNT_ID}>
                Amount to give
              </label>
              <input
                id={REQUEST_AMOUNT_ID}
                className="amount__input"
                type="text"
                inputMode="decimal"
                placeholder="$0.00"
                value={
                  requestAmountText ??
                  formatMoney({ amountMinor: paymentRequest.amountMinor, currency: "USD" })
                }
                onChange={(changed) => {
                  const raw = changed.target.value;
                  setRequestAmountText(raw);
                  const parsed = parseAmountToMinor(raw);
                  if (parsed && BigInt(parsed) >= 100n) setAmountMinor(parsed);
                }}
              />
            </div>
          ) : customOpen ? (
            <div className={`amount amount--field${spanningLastCell}`}>
              <label className="visually-hidden" htmlFor={CUSTOM_AMOUNT_ID}>
                Another amount
              </label>
              <input
                id={CUSTOM_AMOUNT_ID}
                className="amount__input"
                type="text"
                inputMode="decimal"
                placeholder="$0.00"
                autoFocus
                value={customAmount}
                onChange={(changed) => {
                  const raw = changed.target.value;
                  setCustomAmount(raw);
                  const parsed = parseAmountToMinor(raw);
                  if (parsed) setAmountMinor(parsed);
                }}
              />
            </div>
          ) : (
            <button
              type="button"
              className={`amount amount--other${spanningLastCell}`}
              onClick={() => setCustomOpen(true)}
            >
              Another amount…
            </button>
          )}
        </div>
      </fieldset>

      {/*
        The additions line (owner 2026-10-10, GH-938: "organize all the give card icons so they
        all use up one line" — the same line iOS `GiveCard.additions` draws): the **note** icon and
        the **designation** folder at the leading edge, and the **name on file** — the signed-in
        default — at the trailing edge, readable at a glance. An open note's field takes the line
        in the name's place and gives it back when folded; the designation's chooser expands under
        the line. Absent where the server would refuse the lot: a held gift.

        The note stays off the pledge-first path (the server refuses one on a promise), and the
        designation stays off a payment request (the request's own designation rides its token).
      */}
      {held === null && (
        <div className="give-additions">
          {!pledgeFirst && (
            <button
              type="button"
              className={`give-additions__icon${
                noteOpen || note.trim() !== "" ? " give-additions__icon--active" : ""
              }`}
              aria-label={note.trim() !== "" ? GIVE_CARD_COPY.editNote : GIVE_CARD_COPY.addNote}
              aria-expanded={noteOpen}
              onClick={() => setNoteOpen((open) => !open)}
            >
              <NoteIcon />
            </button>
          )}
          {/*
            What the gift supports, behind the folder (owner 2026-10-10: "NOT shown by default");
            it sat on the page from build 27 until now. Active when open or when a designation is
            in force — the donor's own, or one pinned by the code or the event — so a decided gift
            is never a blank icon. DEC-043's rule still lives in `DesignationPicker` alone.
          */}
          {paymentRequest === null && choice.kind !== "none" && !noteOpen && (
            <button
              type="button"
              className={`give-additions__icon${
                designationOpen || designationId !== null || choice.kind === "fixed"
                  ? " give-additions__icon--active"
                  : ""
              }`}
              aria-label={GIVE_CARD_COPY.whatItSupports}
              aria-expanded={designationOpen}
              onClick={() => setDesignationOpen((open) => !open)}
            >
              <FolderIcon />
            </button>
          )}
          {noteOpen && !pledgeFirst ? (
            /*
              The note takes the name's place on the line while it is being written (owner
              2026-10-10; GH-793 for the note itself, GH-809 for this screen's). The × at the
              field's left removes the note and its tick; folding the icon keeps what was typed —
              the filled icon says something is there.
            */
            <div className="donor-note">
              <div className="donor-note__row">
                <button
                  type="button"
                  className="donor-note__remove"
                  aria-label="Remove note"
                  onClick={() => {
                    setNote("");
                    setNoteOpen(false);
                    setNoteOnScreen(false);
                  }}
                >
                  &times;
                </button>
                <label className="visually-hidden" htmlFor={DONOR_NOTE_ID}>
                  Note to {context.organization.displayName}
                </label>
                <textarea
                  id={DONOR_NOTE_ID}
                  className="donor-note__input"
                  rows={2}
                  dir="auto"
                  autoFocus
                  aria-describedby={DONOR_NOTE_COUNT_ID}
                  placeholder="Note to the organization"
                  value={note}
                  onChange={(changed) => setNote(capNote(changed.target.value))}
                />
              </div>
              <span id={DONOR_NOTE_COUNT_ID} className="donor-note__count" aria-live="polite">
                {[...note].length}/{DONOR_NOTE_MAX_LENGTH}
              </span>
              {/*
                GH-887: the donor's own, unticked ask to show the note on the event's big screen.
                Its own checkbox, never implied by writing a note; the ⓘ says the organization
                reviews it first and that an anonymous gift's note shows with no name. Offered
                since 2026-10-10 (NOTE_ON_SCREEN_OFFERED; counsel's review of the policy text
                stays on the open-actions list).
              */}
              {NOTE_ON_SCREEN_OFFERED && (
                <div className="donor-note__screen">
                  <label className="smallcheck">
                    <input
                      type="checkbox"
                      checked={noteOnScreen}
                      onChange={(changed) => setNoteOnScreen(changed.target.checked)}
                    />
                    <span>{GIVE_CARD_COPY.noteOnScreen}</span>
                  </label>
                  <ExplainMore title={GIVE_CARD_COPY.noteOnScreenExplainTitle}>
                    {NOTE_ON_SCREEN_EXPLANATION.map((sentence) => (
                      <p key={sentence}>{sentence}</p>
                    ))}
                  </ExplainMore>
                </div>
              )}
            </div>
          ) : (
            <>
              <span className="give-additions__spacer" aria-hidden="true" />
              {nameEditing ? (
                <>
                  <label className="visually-hidden" htmlFor={SHOWN_NAME_ID}>
                    Name to show publicly
                  </label>
                  <input
                    id={SHOWN_NAME_ID}
                    className="give-name__input"
                    type="text"
                    maxLength={RECOGNITION_NAME_MAX_LENGTH}
                    autoComplete="name"
                    placeholder={GIVE_CARD_COPY.nameField}
                    dir="auto"
                    autoFocus
                    value={displayName}
                    onChange={(changed) => {
                      donorChoseName.current = true;
                      setDisplayName(changed.target.value);
                    }}
                  />
                  {/*
                    The cycle's third tap, still where the control was (owner 2026-10-10): back to
                    the name on file — or, for a guest, to what the field holds.
                  */}
                  <button
                    type="button"
                    className="give-additions__icon give-additions__icon--active"
                    aria-label="Use the name on file"
                    onClick={cycleName}
                  >
                    <PersonIcon />
                  </button>
                </>
              ) : (
                /*
                  The name this gift travels with, the name to the LEFT of the person icon (owner
                  2026-10-10). Not a switch any more: the cycle has three stops — named →
                  anonymous → edit — so it is a button whose accessible name reports the state
                  that actually travels and what the next tap does.
                */
                <button
                  type="button"
                  className="give-name"
                  aria-label={
                    recognition.mode === "donor_name"
                      ? `Shown as ${shownAs} — give anonymously instead`
                      : "Shown as Anonymous — add your name"
                  }
                  onClick={cycleName}
                >
                  <span className="give-name__word" dir="auto">
                    {recognition.mode === "donor_name" ? shownAs : GIVE_CARD_COPY.anonymous}
                  </span>
                  {recognition.mode === "donor_name" ? <PersonIcon /> : <AnonymousIcon />}
                </button>
              )}
            </>
          )}
        </div>
      )}

      {/*
        The one line the controls above cannot show by their shape (unchanged from the grid-cell
        era, 2026-09-16): what "Anonymous" means (GH-858, DEC-049 — hidden from the public, not
        from the organization), the in-between state while the field is empty, and the way to
        forget a remembered device name. A slot, so a sentence arriving moves nothing below it.
      */}
      {held === null && (
        <div className="give__hint">
          {recognition.mode !== "donor_name" && !publishName && (
            <Explain
              className="give__hint-explain"
              label={ANONYMITY_COPY.publicShort}
              title={ANONYMITY_COPY.publicExplanationTitle}
            >
              <p>{ANONYMITY_COPY.publicExplanation(context.organization.displayName)}</p>
            </Explain>
          )}
          {publishName && recognition.mode !== "donor_name" && (
            <span>Anonymous until you type a name.</span>
          )}
          {/* Only when a name arrived from a previous gift on this device, so the donor can see
              it is remembered and get rid of it. */}
          {publishName && remembered && displayName === remembered && (
            <button
              type="button"
              className="link link--right"
              onClick={() => {
                donorChoseName.current = true;
                setDisplayName("");
                forgetDisplayName();
              }}
            >
              Not you? Clear
            </button>
          )}
        </div>
      )}

      {/*
        The chooser, expanded in place under the line. The picker, the pinned line and their rule
        are `DesignationPicker`'s alone (DEC-043); `selectedId` is the donor's own answer, not the
        wire's — `chosenDesignationId` deliberately drops a pinned id, and the screen still has to
        show what is in force. The "Named on your receipt" ⓘ keeps saying only what the server
        genuinely does with the answer — it rides the quote, is stored on the donation, and is
        snapshotted onto the receipt — never "spent only on" (DEC-028 Q1, DEC-043's referred
        question; the longer history is in this file's git log at build 27).
      */}
      {held === null && paymentRequest === null && designationOpen && !noteOpen && (
        <div className="give-designation">
          <DesignationPicker
            choice={choice}
            selectedId={designationId}
            onChoose={setDesignationId}
          />
          {choice.kind !== "none" && (
            <Explain
              className="designation-note"
              label="Named on your receipt"
              title="What your choice does"
            >
              <p>What you choose here travels with your gift and is named on your receipt.</p>
              <p>The organization is told which one you chose.</p>
            </Explain>
          )}
        </div>
      )}

      {pledgeOnly && <p className="notice">This event has ended. You can still pledge.</p>}

      {error && (
        <p className="notice notice--error" role="alert">
          {error}
        </p>
      )}

      {/*
        Before the pay action, in full and in the server's words (GH-798, DEC-046): who the gift is
        made to and what it is targeted for, then the terms. On the page rather than behind an ⓘ,
        because this is the one checkout where the organization on the header is not the recipient,
        and a donor must be able to read that before pressing anything. Never reworded here — the
        sentences carry the configured recipient and hold length, and a paraphrase is how a screen
        ends up telling a donor the organization already has the money.
      */}
      {held !== null && (
        <section className="held-gift" aria-label="Where your gift goes">
          <p className="badge badge--muted">{context.organization.verificationLabel}</p>
          <p>{held.notice}</p>
          <p className="muted small">{held.terms}</p>
        </section>
      )}

      {/*
        A returning donor's button says how it will pay and does it in one tap; "another way"
        opens the full sheet. Everyone else gets the sheet, as before. Either way the actual
        charge only ever starts server-side from the signed quote.
      */}
      {/*
          The docked summary and the button under it (owner's screen 2, 2026-09-02).

          `position: sticky` rather than `fixed`. A fixed bar is laid over the end of the page and
          then needs somebody to remember to reserve room for it; every time that is forgotten it
          covers the fine print, which on this screen is the organization's legal name and EIN. A
          sticky element is still in the flow, so the page below it — the DAF door, the fine print —
          cannot be hidden by it, and it still rides the bottom of the viewport for as long as
          there is anything after it to scroll.

          It puts the total one glance above the button that commits it, which is what iOS already
          does (`GiveView.safeAreaInset(edge: .bottom)`): the addition and the number it is added
          to have to be read together or the addition is not really a choice.
        */}
      <div className="dock">
        {/*
            Every figure here is the server's, read straight off the quote. Nothing on this screen
            adds two numbers together — that is the one arithmetic rule this bundle has, and a
            summary is precisely where a client starts breaking it.

            The rows reuse the receipt's own `.totals` / `.row` / `.row--total`, so the sentence a
            donor reads before paying and the one they read after are set the same way.
          */}
        {quote && (
          <div className="totals">
            <div className="row">
              <span>
                {/*
                    The line names what the donor tapped (UX review 2026-09-17). It read
                    "Donation — General", and "General" is the server's default *designation*
                    name — but a donor who arrived from an event page called "Real CID QA Drive"
                    reads it as "this went to the general fund", which is the exact wrong-turn
                    the 2026-09-16 flow fix was for. The event's name is tenant-authored, so it
                    reads its own direction.
                  */}
                <span dir="auto">{context.event?.name ?? "Donation"}</span>
                {/* The server's word for what this gift supports, and only where the donor had
                      a choice to make — one designation is not a designation, it is the default,
                      and naming it beside the event is what made the line mislead. Never
                      assembled here from the picker's own state: the receipt says what the server
                      says. */}
                {quote.designationName && context.designations.length > 1 ? (
                  <>
                    {" — "}
                    <span dir="auto">{quote.designationName}</span>
                  </>
                ) : null}
              </span>
              <span>{formatMoney(quote.donation)}</span>
            </div>
            {/*
                Cost support lives in the bill now, not in a card above it (owner-directed
                2026-09-02).

                It used to be a bordered checkbox row of its own, plus an ⓘ line under that — a
                whole section of a phone screen restating a number the summary was about to show
                anyway. It is now the line it always described: the fee sits between the donation
                and the total, where a donor reads it in the arithmetic, and the control is an **×**
                against the amount, which is what "remove this line from my bill" looks like
                everywhere else money is itemised.

                **Both states are still reachable and still one tap**, which SPEC.md §10.4 requires
                of a pre-selected addition: added, the row carries the × that removes it; removed,
                the row becomes the offer that adds it back. The row is present either way, so the
                donor is never left wondering where the choice went — the old rule that hid it at
                $0.00 hid the way back with it.

                **The words are still the server's** — `costSupportShortLabel` when it is on the
                bill, `costSupportOfferShortLabel` when it is being offered — both composed against
                the fee this quote was actually priced with. Nothing here computes, names or
                characterises a fee (DEC-028).

                **`checked` is the quote's, never the local flag.** The server decides whether
                support is on this gift the same way it decides the total; a control driven by local
                intent would show a state the money did not have for the length of a re-quote.

                The ⓘ sits on the row rather than under the block, which is the whole point of
                DEC-034: the arithmetic for declining must be reachable from the state where it is
                already accepted, not only from the one where it is not.
              */}
            {/*
                A promise is not a bill (owner-directed 2026-09-02). Nothing is processed, so there
                is no processing cost to support and no total to charge — the only true figure is
                the donation, and `PledgeFlow` is handed exactly that. Showing "Total $26.12" above
                a button reading "Promise $25.00" would be two different numbers describing one
                act, with the larger one wrong.
              */}
            {!pledgeFirst && (
              <div className="row row--total">
                <span>Total</span>
                <span>{formatMoney(quote.total)}</span>
              </div>
            )}
          </div>
        )}
        {/*
          Whether the donor covers the fee: a small checkbox directly above the button (owner,
          build 27, GH-817 — the iPhone's give card draws the same). It was a bill row with an ×
          or an "Add". **The words are still the server's** — `costSupportShortLabel` when it is
          on the gift, with the server's figure, `costSupportOfferShortLabel` when it is offered —
          and **`checked` is the quote's, never the local flag**, so the box never shows a state
          the money did not have for the length of a re-quote. The ⓘ keeps both halves of
          DEC-034 one tap away.
        */}
        {quote && !pledgeFirst && held === null && (
          <div className="smallcheck smallcheck--fee">
            <label>
              <input
                type="checkbox"
                id={COST_SUPPORT_ID}
                checked={quote.costSupportSelected}
                onChange={() => setCostSupport(!quote.costSupportSelected)}
              />
              {/* GH-878 (owner, build 36): "Cover processing", the same words as the app's give
                  card, right-aligned to the button's edge; what it costs and what declining does
                  stay the server's, behind the ⓘ. */}
              <span>{GIVE_CARD_COPY.coverProcessing}</span>
              {quote.costSupportSelected && (
                <span className="smallcheck__figure"> +{formatMoney(quote.costSupport)}</span>
              )}
            </label>
            <FeeExplain quote={quote} />
          </div>
        )}
        {/*
          A returning donor who is signed out (GH-856 c): the way back to their saved card, above
          the button and at the button's own size — the quieter sibling (`pay--secondary`), so the
          give button keeps the only fill. The marker behind it is one flag and names nobody.
        */}
        {offerSignIn && returning && !busy && (
          <button
            type="button"
            className="pay pay--secondary returning-signin"
            onClick={() => setPanelView("profile")}
          >
            Welcome back — sign in
          </button>
        )}
        {/*
          Apple Pay leads on an Apple device (GH-856 d). On the real rail it is Stripe's own Apple
          Pay button — the only thing that may open the wallet sheet — mounted here in place of the
          give button; Google Pay stays in the sheet. If Stripe reports no wallet after all, the
          ordinary button comes back.
        */}
        {/*
          The main control and, where the main control would not open the sheet itself, the gear
          segment beside it (owner 2026-10-10): the chooser is an options door — a pledge and a
          fund grant live behind it too — so it is a gear, never a card.
        */}
        <div className="pay-split">
          {applePayFirst && walletExpress && primaryWallet !== "unavailable" && !busy ? (
            <div className="primary-wallet" data-apple-pay-primary="">
              <WalletExpressBoxes
                amountMinor={walletExpress.amountMinor}
                currency={walletExpress.currency}
                createIntent={walletExpress.createIntent}
                onAuthorized={walletExpress.onAuthorized}
                {...(walletExpress.onFailed ? { onFailed: walletExpress.onFailed } : {})}
                googlePay="never"
                onAvailability={(available) =>
                  setPrimaryWallet(available ? "available" : "unavailable")
                }
              />
            </div>
          ) : applePayFirst && !walletExpress ? (
            <button
              type="button"
              className="wallet wallet--apple primary-wallet__button"
              disabled={!quote || busy}
              aria-label={quote ? `Apple Pay ${formatMoney(quote.total)}` : "Apple Pay"}
              onClick={() => void pay("apple_pay")}
            >
              {busy ? (
                "Working…"
              ) : (
                <>
                  <span aria-hidden="true"></span> Pay
                </>
              )}
            </button>
          ) : (
            <button
              type="button"
              className="pay"
              disabled={!quote || busy}
              onClick={() =>
                /*
                A donor who came to promise goes straight to the promise. No payment sheet, no
                saved card, no wallet — none of which are on the path they chose, and the saved-card
                branch below would otherwise **charge them on this tap**, which is the exact
                opposite of what the button they pressed said.
              */
                pledgeFirst
                  ? setStatus("pledging")
                  : // A remembered wallet still goes through the sheet on the real rail: the wallet
                    // sheet can only be opened by Stripe's own button there, and faking that with the
                    // card form was exactly the two-screen bug this flow replaced.
                    defaultInstrument
                    ? void paySaved(defaultInstrument)
                    : defaultMethod && (defaultMethod === "card" || !walletExpress)
                      ? void pay(defaultMethod)
                      : setSheetOpen(true)
              }
            >
              {/*
              Two labels, because there are two different presses behind this button.

              The design's "Continue to payment" is true of the ordinary path — the press opens the
              payment sheet and nothing is charged yet — and it is what a donor with no method on
              file now reads, with the total sitting directly above it. It is **false** of the
              returning donor whose saved card or remembered wallet charges on this very tap, so
              that press keeps the label that says what it does and how much: naming the amount on
              the control that spends it is not decoration when the next screen is the receipt.
            */}
              {busy
                ? "Working…"
                : pledgeFirst
                  ? // Never a total: a promise carries no cost support and nothing is processed, so
                    // the figure that belongs on this control is the donation alone. `PledgeFlow`
                    // is handed exactly the same number.
                    quote
                    ? `Promise ${formatMoney(quote.donation)}`
                    : "Promise an amount"
                  : quote && defaultInstrument
                    ? `Give ${formatMoney(quote.total)} · ${instrumentLabel(defaultInstrument)}`
                    : quote && defaultMethod
                      ? `Give ${formatMoney(quote.total)} · ${PAY_METHOD_LABELS[defaultMethod]}`
                      : "Continue to payment"}
            </button>
          )}
          {showsChooser && (
            <button
              type="button"
              className="pay pay-chooser"
              aria-label="Other ways to pay"
              disabled={!quote || busy}
              onClick={() => setSheetOpen(true)}
            >
              <GearIcon />
            </button>
          )}
        </div>
        {/*
          Under the button, one line (owner 2026-10-10): "Pledge instead" at the leading edge —
          the promise reachable without the method sheet — and the small Terms link at the
          trailing edge, where the eye leaves the button. Terms keeps the whole line, centred,
          where no pledge is on offer (GH-817's single word).
        */}
        <div className={showsPledgeLink ? "give-foot" : undefined}>
          {showsPledgeLink && (
            <button
              type="button"
              className="link give-foot__pledge"
              disabled={!quote || busy}
              onClick={() => setStatus("pledging")}
            >
              {GIVE_CARD_COPY.pledgeInstead}
            </button>
          )}
          <GivingTermsNote />
        </div>
        {/*
            The way back across from the promise path. A donor who opened it may still want to
            give now; not after the event, where there is no paying rail to give with (GH-800).
          */}
        {pledgeFirst && !busy && !pledgeOnly && (
          <button type="button" className="link link--center" onClick={() => setSheetOpen(true)}>
            or give now instead
          </button>
        )}
        {/* The everyday door to a saved card, for a signed-out donor (GH-856 c). Five words. */}
        {offerSignIn && !returning && !busy && (
          <button
            type="button"
            className="link link--center"
            onClick={() => setPanelView("profile")}
          >
            Sign in for saved cards
          </button>
        )}
      </div>

      {/* The DAF door, only where the platform enabled it for this organization. Chariot draws
          its own branded button; the chosen amount above prefills the modal. */}
      {/* Gated on a chosen amount too: a grant recommendation with no amount is not a thing
          Chariot's modal can open with, and a null must never reach a payment surface. */}
      {held === null && !payingPledge && context.daf && amountMinor && (
        <DafPayButton
          connectId={context.daf.connectId}
          contextToken={contextToken}
          amountMinor={amountMinor}
          donorToken={validToken}
          suggestedName={publishName ? displayName : null}
          suggestedEmail={donorSession?.profile.email ?? null}
          onCaptured={setDafDone}
        />
      )}

      {/*
        Under the button, in the smallest type on the page: who is receiving this, identified the
        way an organization is legally identified.

        The EIN stands alone. Nothing here says "tax deductible" or "501(c)(3)" — an EIN is an
        identifier, not evidence of exempt status, and the difference is the sort of claim counsel
        makes, not a checkout screen (DEC-001, DEC-011).
      */}
      <footer className="fineprint">
        {/* A held gift's recipient is the fund the receipt will name, not the organization above. */}
        {held !== null ? (
          <span>{held.recipientName}</span>
        ) : (
          <span>
            {context.organization.legalName}
            {context.organization.ein ? ` · EIN ${context.organization.ein}` : ""}
          </span>
        )}
        <span className="fineprint__actions">
          <button
            type="button"
            className="fineprint__icon"
            aria-label="About this organization and event"
            onClick={() => setInfoOpen(true)}
          >
            <InfoIcon />
          </button>
        </span>
      </footer>

      {panelView !== null && context.event && (
        <DonorPanel
          token={validToken}
          session={donorSession}
          view={panelView}
          eventName={context.event.name}
          previewPrompts={prompts}
          menuLinks={[
            // Same retarget as the ticket page's menu, and kept identical to it on purpose:
            // two donor menus that disagree about what one address opens is how the next
            // reader concludes one of them is broken. A held gift's event is the draft holding
            // fund, so its way back is the organization's page, as the back link above says.
            held !== null
              ? {
                  href: `/o/${encodeURIComponent(context.organization.id)}`,
                  label: "Organization page",
                  external: true,
                }
              : {
                  href: `/e/${encodeURIComponent(context.event.id)}`,
                  label: "Event page",
                  external: true,
                },
            { href: "/", label: "Find another event" },
          ]}
          // A room only where the server says this is a live event (GH-856). The general fund
          // and a held gift's fund are not places anyone can be, and checking in to one was the
          // "Event not found" the owner saw on the profile sheet.
          hereEventId={arrivalSpent || !eventScoped ? null : context.event.id}
          allowEventPicker={eventScoped}
          onJoinConsumed={() => setArrivalSpent(true)}
          entries={panelEntries}
          onSession={(token, session) => {
            setValidToken(token);
            setDonorSession(session);
          }}
          onSignedIn={(session) => setSignedInAs(donorName(session))}
          onSignedOut={() => {
            setValidToken(null);
            setDonorSession(null);
            setPanelView(null);
          }}
          onDismiss={() => setPanelView(null)}
        />
      )}

      {infoOpen && (
        <OrgInfoPanel
          organizationId={context.organization.id}
          onDismiss={() => setInfoOpen(false)}
        />
      )}

      {/*
        The figure is the server-signed quote's: the total it will charge.

        `sheetAmount` is null until an amount is chosen, and the button that opens the sheet is
        disabled until then, so the sheet cannot open on a page with no number on it.
      */}
      {sheetOpen && sheetAmount && (
        <PaymentSheet
          total={sheetAmount.total}
          pledgeAmount={sheetAmount.donation}
          organizationDisplayName={context.organization.displayName}
          methods={offeredMethods}
          savedInstruments={held === null ? instruments : []}
          busy={status === "paying"}
          error={error}
          walletExpress={walletExpress}
          onChoose={choose}
          onPaySaved={(instrument) => void paySaved(instrument)}
          onDismiss={() => setSheetOpen(false)}
        />
      )}
    </main>
  );
}
